import { UnitTypes } from 'unitData/types';
import type { LMScreenFamily } from 'store/units/types';
// Import unit images
import { getImageFeed } from 'unitData/Feed/getImageFeed';
// Crushers
import { getImageJawCrusherCJ } from 'unitData/Crushers/CrusherJawCJ/getImageJawCrusherCJ';
import { getImageConeCrusherCS } from 'unitData/Crushers/ConeCrusherCS/getImageConeCrusherCS';
import { getImageCrusherCH } from 'unitData/Crushers/CrusherConeCH/getImageCrusherCH';
import { getImageImpactCrusherCI } from 'unitData/Crushers/ImpactCrusherCI/getImageImpactCrusherCI';
import { getCrusherVSIImage } from 'unitData/Crushers/CrusherVSI/CrusherVSIData';
import { getHybridCrusherCRImage } from 'unitData/Crushers/HybridCrusherCR/getImageHybridCrusherCR';
import { getImageGyratoryCrusherCG } from 'unitData/Crushers/GyratoryCrusherCG/getImageGyratoryCrusherCG';
// Screens
import { getImageCircularMotionScreen } from 'unitData/CircularMotionScreen/getImageCircularMotionScreen';
import { getImageLinearMotionScreen } from 'unitData/LinearMotionScreen/getImageLinearMotionScreen';
import { getImageMultislopeScreen } from 'unitData/MultislopeScreen/getImageMultislopeScreen';
import { getImageBananaScreen } from 'unitData/BananaScreen/getImageBananaScreen';
import { getImageGrizzlyScreenSG } from 'unitData/GrizzlyScreenSG/getImageGrizzlyScreenSG';
import { getImageKwataniScreen } from 'unitData/KwataniScreen/getImageKwataniScreen';
import { getImageFreeFallScreenSF } from 'unitData/FreeFallScreenSF/freeFallScreenSFData';
import { getImageFreeFallScreenSS } from 'unitData/FreeFallScreenSS/freeFallScreenSSData';
// Feeders
import { getImagePanFeeder } from 'unitData/Feeders/Pan/getImagePanFeeder';
import { getImagePlateFeeder } from 'unitData/Feeders/Plate/getImagePlateFeeder';
import { getImageGrizzlyFeeder } from 'unitData/Feeders/Grizzly/getImageGrizzlyFeeder';
import { getImageFeederAndScreen } from 'unitData/Feeders/FeederScreen/getImageFeederScreen';
// Storage
import {
  getImageStorage,
  getImageStorageWith1Output,
  getImageStorageWith2Outputs,
} from 'unitData/Storage/StorageData';
// Generic
import { getImageGenericCrusher } from 'unitData/Generic/GenericCrusher/getImageGenericCrusher';
import { getImageGenericScreen } from 'unitData/Generic/GenericScreen/getImageGenericScreen';
// Mobile
import { getImageMobileJaw } from 'unitData/Mobile/Jaw/getImageMobileJaw';
import { getImageMobileCone } from 'unitData/Mobile/Cone/getImageMobileCone';
import { getImageMobileHSI } from 'unitData/Mobile/HSI/getImageMobileHSI';
import { getImageMobileScreen } from 'unitData/Mobile/Screen/getImageMobileScreen';
import { getImageMobileJawAndCone } from 'unitData/Mobile/JawAndCone/getImageMobileJawAndCone';
import { getImageMobileVSI } from 'unitData/Mobile/VSI/getImageMobileVSI';
// Breaker
import { getImageBreaker } from 'unitData/Breaker/getImageBreaker';
// Add/Split
import { getImageAdd } from 'unitData/Add/getImageAdd';
import { getImageSplit } from 'unitData/Split/getImageSplit';

export interface GetUnitImageOptions {
  variantId?: number;
  showCustomModel?: boolean;
  useHighBin?: boolean;
  modelName?: string;
  nbrOfDecks?: number;
  screenFamily?: LMScreenFamily;
}

/**
 * Returns the image source for a given unit type and optional parameters.
 * This function centralizes all unit image logic for consistent usage across
 * the canvas, preview components, and other UI elements.
 */
export const getUnitImage = (
  type: UnitTypes,
  options: GetUnitImageOptions = {},
): string | null => {
  const {
    variantId,
    showCustomModel = false,
    useHighBin = false,
    modelName = '',
    nbrOfDecks = 2,
    screenFamily = 'SL',
  } = options;

  try {
    switch (type) {
      // Feed
      case UnitTypes.FEED:
        return getImageFeed(variantId ?? 1);

      // Crushers
      case UnitTypes.JAW_CRUSHER_CJ:
        return getImageJawCrusherCJ(modelName, showCustomModel);
      case UnitTypes.CONE_CRUSHER_CS:
        return getImageConeCrusherCS(showCustomModel);
      case UnitTypes.CONE_CRUSHER_CH:
        return getImageCrusherCH(showCustomModel, useHighBin, modelName);
      case UnitTypes.IMPACT_CRUSHER_CI:
        return getImageImpactCrusherCI(showCustomModel);
      case UnitTypes.IMPACT_CRUSHER_CV:
        return getCrusherVSIImage(showCustomModel);
      case UnitTypes.HYBRID_CRUSHER_CR:
        return getHybridCrusherCRImage();
      case UnitTypes.GYRATORY_CRUSHER_CG:
        return getImageGyratoryCrusherCG(showCustomModel, useHighBin);

      // Screens
      case UnitTypes.CONVENTIONAL_SCREEN:
        return getImageCircularMotionScreen(nbrOfDecks, modelName);
      case UnitTypes.LINEAR_MOTION_SCREEN:
        return getImageLinearMotionScreen(nbrOfDecks, screenFamily);
      case UnitTypes.MULTISLOPE_SCREEN:
        return getImageMultislopeScreen(nbrOfDecks, modelName);
      case UnitTypes.BANANA_SCREEN:
        return getImageBananaScreen(nbrOfDecks);
      case UnitTypes.GRIZZLY_SCREEN:
        return getImageGrizzlyScreenSG(nbrOfDecks);
      case UnitTypes.KWATANI_SCREEN:
        return getImageKwataniScreen(nbrOfDecks);
      case UnitTypes.FREEFALL_SCREEN_SF:
        return getImageFreeFallScreenSF(nbrOfDecks || 3);
      case UnitTypes.FREEFALL_SCREEN_SS:
        return getImageFreeFallScreenSS(nbrOfDecks || 3);

      // Feeders
      case UnitTypes.PAN_FEEDER_SP:
        return getImagePanFeeder();
      case UnitTypes.PLATE_FEEDER_SH:
        return getImagePlateFeeder(modelName);
      case UnitTypes.GRIZZLY_FEEDER_SW:
      case UnitTypes.GRIZZLY_FEEDER_SV:
      case UnitTypes.GRIZZLY_FEEDER_SV_E:
        return getImageGrizzlyFeeder();
      case UnitTypes.FEEDER_AND_SCREEN:
        return getImageFeederAndScreen();

      // Storage
      case UnitTypes.STORAGE:
        return getImageStorage(variantId ?? 1);
      case UnitTypes.STORAGE_FEEDER:
        return getImageStorageWith1Output(variantId ?? 3);
      case UnitTypes.STORAGE_FEEDER_2_OUTPUTS:
        return getImageStorageWith2Outputs(variantId ?? 2);

      // Generic

      case UnitTypes.GENERIC_CRUSHER:
        return (
          getImageGenericCrusher({ modelName, useHighBin }) ||
          getImageGenericCrusher({ modelName: 'Cone Crusher', useHighBin })
        );
      case UnitTypes.GENERIC_SCREEN:
        return (
          getImageGenericScreen({ nbrOfDecks, modelName }) ||
          getImageGenericScreen({ nbrOfDecks: 1, modelName: 'Inclined' })
        );

      // Mobile Tracked & Wheeled
      case UnitTypes.MOBILE_TRACKED_JAW:
      case UnitTypes.MOBILE_WHEELED_JAW:
        return getImageMobileJaw(0, modelName);
      case UnitTypes.MOBILE_TRACKED_CONE:
      case UnitTypes.MOBILE_WHEELED_CONE:
        return getImageMobileCone(modelName, 0);
      case UnitTypes.MOBILE_TRACKED_HSI:
      case UnitTypes.MOBILE_WHEELED_HSI:
        return getImageMobileHSI(modelName, 0);
      case UnitTypes.MOBILE_TRACKED_SCREEN:
      case UnitTypes.MOBILE_WHEELED_SCREEN:
        return getImageMobileScreen(0, modelName);
      case UnitTypes.MOBILE_JAW_AND_CONE:
        return getImageMobileJawAndCone();
      case UnitTypes.MOBILE_VSI:
        return getImageMobileVSI(modelName, 0);

      // Breaker
      case UnitTypes.BREAKER:
        return getImageBreaker();

      // Add/Split
      case UnitTypes.ADD:
        return getImageAdd(variantId ?? 2);
      case UnitTypes.SPLIT:
        return getImageSplit(variantId ?? 2);

      // Text Box
      case UnitTypes.FREE_TEXT_BOX:
        return null; // Text box has no image

      default:
        return null;
    }
  } catch {
    return null;
  }
};
